/**
 * Account service module that handles all account-related operations using Parse backend.
 * This module provides functionality for creating, reading, updating, and deleting accounts,
 * as well as managing account balances and profile photos.
 */

import Parse from "./parse.service";
import { auth } from "./index";

const Account = Parse.Object.extend("Account");

/**
 * Creates a new account for the current user
 * @param {string} name - The name of the account
 * @param {string} [phone=""] - The phone number associated with the account
 * @param {string} [address=""] - The address associated with the account
 * @param {string} [details=""] - Additional details about the account
 * @param {string} [tag=""] - Tag/category for the account
 * @param {string} [photoUrl=""] - URL of the account's profile photo
 * @returns {Promise<Parse.Object>} The newly created account object
 * @throws {Error} If account creation fails
 */
export const createAccount = async (
    name,
    phone = "",
    address = "",
    details = "",
    tag = "",
    photoUrl = ""
  ) => {
    console.log("photoUrl", photoUrl);
    const newAccount = new Account();
    if(name) newAccount.set("name", name);
    if(phone) newAccount.set("phone", phone);
    if(address) newAccount.set("address", address);
    if(details) newAccount.set("details", details);
    if(tag) newAccount.set("tag", tag);
    if(photoUrl) newAccount.set("photo", photoUrl);

    try {
      newAccount.set("userId", auth.fetchUser());
      newAccount.set("balance", 0);
      newAccount.set("cancelled", false);
      return await newAccount.save();
    } catch (error) {
      throw new Error("Failed to create account");
    }
  };
  
/**
 * Retrieves all accounts for the current user
 * @returns {Promise<Parse.Object[]>} Array of account objects
 * @returns {null} If no current user is found
 */
export const getAccounts = async () => {
    const currentUser = auth.fetchUser();
    if(!currentUser) return null;
    const query = new Parse.Query(Account);
    // query.include("userId");
    query.equalTo("userId", currentUser);
    query.exclude("userId");
    query.ascending("name");
    query.ascending("cancelled");
    
    query.limit(1000000);
  
    // if (hideClosed) query.equalTo("cancelled", false);
    return await query.find();
  }
  
/**
 * Retrieves all active (non-cancelled) accounts for the current user
 * @returns {Promise<Parse.Object[]>} Array of active account objects
 */
export const getActiveAccounts = async () => {
    const query = new Parse.Query(Account);
    query.equalTo("cancelled", false);
    query.ascending("name");
    query.limit(1000000);
  
    query.equalTo("userId", currentUser);
    return await query.find();
  }
  
/**
 * Finds an account by its exact name (case-insensitive)
 * @param {string} name - The name of the account to find
 * @returns {Promise<Parse.Object|number>} The found account object or 0 if not found
 */
export const getAccountByName = async (name) => {
    const query = new Parse.Query(Account);
    query.limit(1);
    query.matches("name", new RegExp(`^${name}$`, "i"));
    query.equalTo("userId", auth.fetchUser());
    const results = await query.find();  
    if (results.length) return results[0];
    else return results.length;
  }
 
/**
 * Retrieves an account by its Parse object ID
 * @param {string} id - The Parse object ID of the account
 * @returns {Promise<Parse.Object|null>} The found account object or null if not found
 */
export const getAccountById = async (id) => {
    const query = new Parse.Query(Account);
    query.limit(1);
    query.equalTo("objectId", id);
    const results = await query.find();  
    // console.log(results[0]);
    if (results.length != 0) return results[0];
    return null;
  }
  
/**
 * Updates an existing account's information
 * @param {string} accountId - The ID of the account to update
 * @param {string} name - New name for the account
 * @param {string} phone - New phone number
 * @param {string} address - New address
 * @param {string} details - New details
 * @param {string} tag - New tag
 * @param {string} photoUrl - New photo URL
 * @returns {Promise<Parse.Object|null>} Updated account object or null if account not found
 * @throws {Error} If update operation fails
 */
export const updateAccount = async (accountId, name, phone, address, details, tag, photoUrl) => {
    var account = await getAccountById(accountId);
    if(!account) return null;
    try {
      if (account.get("name") !== name) {
        account.set("name", name);
      }
      if (account.get("phone") !== phone) {
        account.set("phone", phone);
      }
      if (account.get("address") !== address) {
        account.set("address", address);
      }
      if (account.get("details") !== details) {
        account.set("details", details);
      }
      if(account.get("photo") !== photoUrl && photoUrl){
        account.set("photo", photoUrl);
      }
      if(account.get("tag") !== tag){
        account.set("tag", tag);
      }
      await account.save();
      return account;
    } catch (error) {
      throw new Error("Failed to update account");
    }
  }
  
/**
 * Marks an account as cancelled/closed
 * @param {string} id - The ID of the account to close
 * @returns {Promise<boolean>} True if the operation was successful
 * @throws {Error} If closing operation fails
 */
export const closeAccount = async (id) => {
    try {
      const account = await getAccountById(id);
      account.set("cancelled", true);
      await account.save();
      return true;
    } catch (error) {
      console.error("Error closing account:", error);
      throw new Error("Failed to close account");
    }
  }
  
/**
 * Permanently deletes an account
 * @param {Parse.Object} acc - The account object to delete
 * @returns {Promise<Parse.Object>} Result of the delete operation
 */
export const deleteAccount = async (acc) => {
    return await acc.destroy();
  }
  
/**
 * Reopens a previously closed account
 * @param {string} id - The ID of the account to reopen
 * @returns {Promise<Parse.Object>} The reopened account object
 * @throws {Error} If reopening operation fails
 */
export const reopenAccount = async (id) => {
    try{
      const account = await getAccountById(id);
      account.set("cancelled", false);
      return await account.save();
    } catch (error) {
      console.error("Error reopening account:", error);
      throw new Error("Failed to re-open account");
    }
  }
  
/**
 * Updates the balance of an account by adding or subtracting an amount
 * @param {string} accountid - The ID of the account to update
 * @param {number} amount - The amount to add (positive) or subtract (negative)
 * @returns {Promise<Parse.Object>} The updated account object
 * @throws {Error} If account is not found or update fails
 */
export const updateBalance = async (accountid, amount) => {
  try {
    const myAccount = await getAccountById(accountid);
    if (myAccount) {
      myAccount.set("balance", myAccount.get("balance") + amount);
      const resposne = await myAccount.save();
      // return await myAccount.save();
      return resposne;
    } else {
      throw new Error("Account not found.");
    }
  } catch (error) {
    console.error("Error updating balance: ", error);
    throw error;
  }
}

/**
 * This function updates the profile photo of an account.
 * It takes an account ID and a file as input, uploads the file to Parse,
 * and sets the URL of the uploaded file as the account's photo.
 *
 * @param {string} accountId - The ID of the account to update.
 * @param {File} file - The file to upload as the new profile photo.
 * @returns {Object} - The result of the file upload.
 * @throws Will throw an error if the file upload or account update fails.
 */
export const updateAccountProfile = async (accountId, file) => {
    const name = file.name;
    try {
        const account = await getAccountById(accountId);
        var parseFile = new Parse.File(name, file);
        const result = await parseFile.save();
        account.set("photo", result._url);
        await account.save();
        return result;
    } catch (error) {
        console.log("error upload file error", error);
        throw error;
    }
}

/**
 * This function retrieves a list of unique tags associated with the accounts of the current user.
 * It combines a set of default tags with the tags from the user's accounts,
 * filters out any undefined or null tags, and ensures the tags are unique.
 *
 * @returns {Array} - A list of unique tags.
 */
export const getTags = async () => {
  const query = new Parse.Query(Account);
  query.equalTo("userId", auth.fetchUser());
  query.exists("tag");
  query.select("tag");
  
  const accounts = await query.find();
  
  const tags = accounts.map(account => account.get("tag"))
    .filter(tag => tag !== undefined && tag !== null);
  return [...new Set(tags)];
}